/* eslint-disable class-methods-use-this */
import { loadErrorMessages } from '@apollo/client/dev';
import {
  getApolloClient,
  removeApolloClient,
  trackSubscription,
  untrackSubscription,
  untrackAllSubscriptions,
} from '@/api/graphql/apolloClientHelpers';

/*
 * Handle setup of a GraphQL client per base domain
 * To be extended by other GraphQL clients which handle their own shared instance of ApolloClient
 */
class BaseGraphQLClient {
  constructor({ baseDomain = import.meta.env.VITE_GRAPHQL_GAIA_DOMAIN } = {}) {
    this.baseDomain = baseDomain;

    // Grab an existing ApolloClient instance or create a new one to be shared
    this.client = getApolloClient({ baseDomain: this.baseDomain });

    if (import.meta.env.VITE_ENV_NAME !== 'production') {
      loadErrorMessages();
    }
  }

  /*
   * Query a GraphQL query taking in an object with the following properties
   * operation - GraphQL Query
   */
  async query({ operation }) {
    try {
      return await this.client.query(operation);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /*
   * Subscribe to a GraphQL subscription taking in an object with the following properties
   * operation - GraphQL Subscription
   * callbackFn - Provided function to call when data is retrieved
   * handleSubscriptionFn - Provided function to call with the tracked subscription
   * onError - Provided function to call when an error occurs
   */
  subscribe({
    operation, callbackFn, handleSubscriptionFn, onError,
  }) {
    let trackedSubscription;
    try {
      const subscription = this.client.subscribe(operation).subscribe({
        next({ data }) {
          // Call the callback function with the data and the origin of the subscription
          if (callbackFn) callbackFn(data, { ...trackedSubscription });
        },
      });

      // When a subscription is created, track and retain it
      const result = trackSubscription({
        baseDomain: this.baseDomain,
        subscription,
        operation,
      });

      trackedSubscription = result.trackedSubscription;

      if (handleSubscriptionFn) handleSubscriptionFn({ ...trackedSubscription });
    } catch (error) {
      if (onError) onError({ error, ...trackedSubscription });
    }
  }

  unsubscribe(trackedSubscriptionId) {
    untrackSubscription({
      baseDomain: this.baseDomain,
      subscriptionId: trackedSubscriptionId,
    });
  }

  unsubscribeAll() {
    // Unsubscribe all active subscriptions
    untrackAllSubscriptions({ baseDomain: this.baseDomain });
  }

  // Stop any ongoing queries or subscriptions
  stopAll() {
    this.unsubscribeAll();
    this.client.stop();
    removeApolloClient({ baseDomain: this.baseDomain });
    this.client = undefined;
  }

  // Provide token to be provided to requests to manually cancel
  cancelController() {
    const controller = new AbortController();

    return {
      token: controller.signal,
      cancel: (reason) => controller.abort(reason),
    };
  }
}

export default BaseGraphQLClient;
